import React, { useState } from "react"
import {
  X,
  CheckSquare,
  Globe,
  FileText,
  GitBranch,
  ActivityIcon as Function,
  Clock,
  Pencil,
  FilePlus,
  Briefcase,
  MessageSquare,
  Mail,
  Lock,
} from "lucide-react"
import { Link } from "@inertiajs/react"
import "./BlocksPanel.css"

const BLOCK_DISPATCH = {
  task:       { selectId: "task",       reactflowType: "taskNode" },
  request:    { selectId: "request",    reactflowType: "requestNode" },
  form:       { selectId: "form",       reactflowType: "formNode" },
  ifElse:     { selectId: "ifElse",     reactflowType: "ifElseNode" },
  action:     { selectId: "action",     reactflowType: "actionNode" },
  wait:       { selectId: "wait",       reactflowType: "waitNode" },
  branch:     { selectId: "branchNode", reactflowType: "branchNode" },
  updateNote: { selectId: "updateNote", reactflowType: "updateNoteNode" },
  updateJob:  { selectId: "updateJob",  reactflowType: "updateJobNode" },
  createNote: { selectId: "createNote", reactflowType: "createNoteNode" },
  sendText:   { selectId: "sendText",   reactflowType: "sendTextNode" },
  sendEmail:  { selectId: "sendEmail",  reactflowType: "sendEmailNode" },
}

const BlocksPanel = ({
  onClose,
  onSelectBlock,
  twilio = { connected: false },
  canSendTexts = false,
  canSendEmails = false,
}) => {
  const [hoveredBlock, setHoveredBlock] = useState(null)

  const sendSmsEnabled = twilio?.connected && canSendTexts
  const sendEmailEnabled = canSendEmails

  const sections = [
    {
      id: "core",
      name: "Core",
      blocks: [
        { id: "task",    name: "Task",         icon: <CheckSquare size={20} />, color: "#10b981" },
        { id: "request", name: "HTTP Request", icon: <Globe size={20} />,       color: "#3b82f6" },
        { id: "form",    name: "Dynamic Form", icon: <FileText size={20} />,    color: "#fbbf24" },
        { id: "ifElse",  name: "If/Else",      icon: <GitBranch size={20} />,   color: "#8b5cf6" },
        { id: "action",  name: "Action",       icon: <Function size={20} />,    color: "#94a3b8" },
        { id: "wait",    name: "Wait",         icon: <Clock size={20} />,       color: "#f59e0b" },
      ],
    },
    {
      id: "messaging",
      name: "Messaging",
      blocks: [
        {
          id: "sendText",
          name: "Send SMS",
          icon: sendSmsEnabled ? <MessageSquare size={20} /> : <Lock size={20} />,
          color: "#ec4899",
          disabled: !sendSmsEnabled,
          disabledReason: !twilio?.connected
            ? "Connect Twilio in Admin → Integrations to send SMS."
            : "You need send_texts permission to use SMS blocks.",
        },
        {
          id: "sendEmail",
          name: "Send Email",
          icon: sendEmailEnabled ? <Mail size={20} /> : <Lock size={20} />,
          color: "#3b82f6",
          disabled: !sendEmailEnabled,
          disabledReason: "You need send_emails permission to use email blocks.",
        },
      ],
    },
    {
      id: "lp",
      name: "LP",
      blocks: [
        { id: "updateNote", name: "Update Note", icon: <Pencil size={20} />, color: "#0ea5e9" },
        { id: "updateJob", name: "Update Job", icon: <Briefcase size={20} />, color: "#6366f1" },
        { id: "createNote", name: "Create Note", icon: <FilePlus size={20} />, color: "#10b981" },
      ],
    },
  ]

  const handleSelect = (block) => {
    if (block.disabled) return
    const dispatch = BLOCK_DISPATCH[block.id] || { selectId: "workflow" }
    onSelectBlock(dispatch.selectId)
    onClose()
  }

  const handleDragStart = (event, block) => {
    if (block.disabled) {
      event.preventDefault()
      return
    }
    const dispatch = BLOCK_DISPATCH[block.id] || { reactflowType: "workflowNode" }
    event.dataTransfer.setData("application/reactflow", dispatch.reactflowType)
    event.dataTransfer.effectAllowed = "move"
  }

  return (
    <div className="blocks-panel dark:bg-card dark:border-border">
      <div className="blocks-panel-header dark:border-b-2 dark:border-[#1f2937]">
        <h2>Blocks</h2>
        <button className="close-button dark:text-white dark:hover:bg-red-500" onClick={onClose}>
          <X size={18} />
        </button>
      </div>
      {sections.map((section) => (
        <div key={section.id} className="blocks-section">
          <h3 className="blocks-section-title dark:text-gray-300">{section.name}</h3>
          <div className="blocks-grid">
            {section.blocks.map((block) => (
              <div
                key={block.id}
                className={`block-item dark:hover:bg-[#1f2937] ${block.disabled ? "block-item-disabled" : ""}`}
                onClick={() => handleSelect(block)}
                draggable={!block.disabled}
                onDragStart={(event) => handleDragStart(event, block)}
                onMouseEnter={() => block.disabled && setHoveredBlock(block.id)}
                onMouseLeave={() => setHoveredBlock(null)}
              >
                <div className="block-icon" style={{ backgroundColor: block.disabled ? "#9ca3af" : block.color, opacity: block.disabled ? 0.6 : 1 }}>
                  {block.icon}
                </div>
                <div className="block-name dark:text-white">{block.name}</div>
                {block.disabled && hoveredBlock === block.id && (
                  <div className="block-disabled-tooltip">
                    <p>{block.disabledReason}</p>
                    {!twilio?.connected && (
                      <Link href="/integrations" className="block-disabled-link">Go to Integrations</Link>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

export default BlocksPanel
